/**
 * Dealer Dashboard — frontend feature registration (task00).
 *
 * ── Why this exists when `/dashboard` already worked ─────────────────────────
 *
 * The route was declared statically in `App.tsx`, so the dashboard page and its whole
 * import graph landed in the **entry** chunk — loaded before first paint for every
 * session, including sessions that never visit it. Moving the declaration here makes it
 * a `lazy()` route with its own chunk, which is what TRD §4's ≤ 120 kB gzip budget is
 * measured against. Tasks 06–07 add real widgets behind that boundary; without it, they
 * would inflate the entry chunk instead.
 *
 * `navItems` is deliberately **empty**: `/dashboard` is already declared in
 * `AppLayout`'s `coreNavItems`, and `AppLayout` concatenates core + feature nav items
 * without deduping, so contributing one here would render "Dashboard" twice in the
 * sidebar.
 *
 * Per the registry INVARIANT, this entry imports only react, react-router-dom,
 * `ProtectedRoute`, `./routeHelpers`, and a `lazy()` page factory — nothing heavier, or
 * the entry chunk re-inflates and the split above is undone.
 */
import React, { lazy } from 'react'
import { Route } from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { lazyRoute } from '../routeHelpers'
import type { FrontendFeature } from '../types'

const DashboardPage = lazy(() => import('./pages/DashboardPage'))

const feature: FrontendFeature = {
  // See the header — `coreNavItems` in AppLayout already provides the Dashboard entry.
  navItems: [],
  routes: [
    <Route
      key="dashboard"
      path="/dashboard"
      element={
        // No `requiredPermissions`: the page is scoped to the caller's own data, and
        // the composite API separately gates on `projects:read`. A permission gate here
        // would lock a user out of their own landing page rather than degrade the
        // sections they cannot see — which is task05's job, per-section.
        <ProtectedRoute>{lazyRoute(<DashboardPage />)}</ProtectedRoute>
      }
    />,
    // NO `/` route here on purpose.
    //
    // task06 asks for `/` to render the dashboard, and it already does: `WelcomePage` (the
    // `/` route in `App.tsx`) redirects authenticated users to `/dashboard` and everyone
    // else to `/login`, with a loading state while auth resolves.
    //
    // Declaring `/` here would be a REGRESSION, not an addition — a bare
    // `<Navigate to="/dashboard">` loses the unauthenticated → `/login` branch and the
    // auth-loading state, and two routes for one path is ambiguous in React Router.
  ],
}

export default feature
